import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  createClient,
  AnamEvent,
  type AnamClient,
  type MessageStreamEvent,
} from '@anam-ai/js-sdk';
import {
  beginQualificationInitialisation,
  completeQualificationRun,
  recordQualificationConnection,
  recordQualificationDisconnect,
  recordQualificationStream,
  recordQualificationToken,
  recordQualificationTranscript,
  type ActiveQualificationRun,
} from '@/lib/networkQualification';

interface AnamConfig {
  personaId: string;
  systemPrompt: string;
  firstMessage?: string;
  /**
   * Optional stable id used to back the transcript up to localStorage.
   * Pass the simulation session id so the transcript survives a page
   * reload or a network drop. Without this, the transcript is in-memory only.
   */
  persistKey?: string;
  onTranscriptUpdate?: (messages: TranscriptMessage[]) => void;
  getContextForUserSpeech?: (utterance: string) => string | null;
  onAssistantSpeechEnd?: (utterance: string) => void;
  onSessionEnd?: () => void;
  onError?: (error: string) => void;
}

interface TranscriptMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

export type AnamConnectionState =
  | 'idle'           // not initialised yet
  | 'connecting'     // session token + client created, waiting for first frame
  | 'connected'      // streaming, healthy
  | 'reconnecting'   // closed unexpectedly within last 30s — SDK may auto-recover
  | 'disconnected';  // closed and not recovered; user action needed

const TRANSCRIPT_STORAGE_PREFIX = 'medd-sim-transcript-';
const RECONNECT_GRACE_MS = 30_000; // how long we treat a CLOSED state as "reconnecting" before flipping to disconnected

function loadCachedTranscript(persistKey?: string): TranscriptMessage[] {
  if (!persistKey) return [];
  try {
    const raw = localStorage.getItem(TRANSCRIPT_STORAGE_PREFIX + persistKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

function saveCachedTranscript(persistKey: string | undefined, messages: TranscriptMessage[]) {
  if (!persistKey) return;
  try {
    localStorage.setItem(TRANSCRIPT_STORAGE_PREFIX + persistKey, JSON.stringify(messages));
  } catch {
    // Quota exceeded or storage unavailable — silently ignore. Transcript
    // is still in component state; only restore-after-reload is lost.
  }
}

export function useAnamAvatar(config: AnamConfig) {
  const [isConnected, setIsConnected] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [connectionState, setConnectionState] = useState<AnamConnectionState>('idle');
  const [lastDisconnectReason, setLastDisconnectReason] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptMessage[]>(() => loadCachedTranscript(config.persistKey));
  const [error, setError] = useState<string | null>(null);

  const anamClientRef = useRef<AnamClient | null>(null);
  const transcriptRef = useRef<TranscriptMessage[]>(transcript);
  const assistantUtterancesRef = useRef<Map<string, string>>(new Map());
  const userUtterancesRef = useRef<Map<string, string>>(new Map());
  const reconnectTimerRef = useRef<number | null>(null);
  const qualificationRunRef = useRef<ActiveQualificationRun | null>(null);

  // Initialize Anam client
  const initialize = useCallback(async () => {
    try {
      setError(null);
      setConnectionState('connecting');
      qualificationRunRef.current = beginQualificationInitialisation();

      // Get session token from edge function with personaId, systemPrompt, and firstMessage
      const { data: sessionData, error: sessionError } = await supabase.functions.invoke(
        'anam-get-session-token',
        {
          body: {
            personaId: config.personaId,
            systemPrompt: config.systemPrompt,
            firstMessage: config.firstMessage,
          },
        }
      );

      if (sessionError || !sessionData?.sessionToken) {
        throw new Error('Failed to get Anam session token');
      }
      recordQualificationToken(qualificationRunRef.current);

      // Create Anam client with session token
      anamClientRef.current = createClient(sessionData.sessionToken);

      // ----- Connection lifecycle listeners -----
      anamClientRef.current.addListener(AnamEvent.CONNECTION_ESTABLISHED, () => {
        if (reconnectTimerRef.current) {
          window.clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
        setConnectionState('connected');
        setIsConnected(true);
        setLastDisconnectReason(null);
        recordQualificationConnection(qualificationRunRef.current);
      });

      anamClientRef.current.addListener(
        AnamEvent.CONNECTION_CLOSED,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (reason: any, details?: string) => {
          const reasonLabel = typeof reason === 'string' ? reason : String(reason ?? 'unknown');
          const detailLabel = details ? `: ${details}` : '';
          const fullReason = `${reasonLabel}${detailLabel}`;
          console.warn('[Anam] connection closed', fullReason);
          setLastDisconnectReason(fullReason);
          setIsConnected(false);
          setConnectionState('reconnecting');
          void recordQualificationDisconnect(fullReason);

          // After the grace window, if we haven't reconnected, flip to disconnected.
          if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = window.setTimeout(() => {
            setConnectionState((prev) => (prev === 'reconnecting' ? 'disconnected' : prev));
          }, RECONNECT_GRACE_MS);
        }
      );

      anamClientRef.current.addListener(AnamEvent.SERVER_WARNING, (message: string) => {
        console.warn('[Anam] server warning:', message);
      });

      // ----- Message stream listener -----
      anamClientRef.current.addListener(
        AnamEvent.MESSAGE_STREAM_EVENT_RECEIVED,
        (event: MessageStreamEvent) => {
          const content = event.content || '';
          const isAssistant =
            event.role === 'persona';
          const assignedRole: 'user' | 'assistant' = isAssistant ? 'assistant' : 'user';

          if (content.trim()) {
            const newMessage: TranscriptMessage = {
              role: assignedRole,
              content,
              timestamp: Date.now(),
            };
            void recordQualificationTranscript(assignedRole);

            const updated = [...transcriptRef.current, newMessage];
            transcriptRef.current = updated;
            saveCachedTranscript(config.persistKey, updated);
            config.onTranscriptUpdate?.(updated);
            setTranscript(updated);
          }

          if (isAssistant) {
            const utterance = `${assistantUtterancesRef.current.get(event.id) || ''}${content}`;
            if (event.endOfSpeech) {
              assistantUtterancesRef.current.delete(event.id);
              if (!event.interrupted) {
                config.onAssistantSpeechEnd?.(utterance.trim());
              }
            } else {
              assistantUtterancesRef.current.set(event.id, utterance);
            }
          } else {
            const utterance = `${userUtterancesRef.current.get(event.id) || ''}${content}`;
            if (event.endOfSpeech) {
              userUtterancesRef.current.delete(event.id);
              if (!event.interrupted && utterance.trim()) {
                const runtimeContext = config.getContextForUserSpeech?.(utterance.trim());
                if (runtimeContext?.trim()) {
                  try {
                    anamClientRef.current?.addContext(runtimeContext);
                  } catch (contextError) {
                    console.warn('[Anam] runtime context unavailable:', contextError);
                  }
                }
              }
            } else {
              userUtterancesRef.current.set(event.id, utterance);
            }
          }
        }
      );

      setIsConnected(true); // optimistic — confirmed by CONNECTION_ESTABLISHED
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown initialise error';
      console.error('Failed to initialize Anam:', err);
      setError(errorMessage);
      setConnectionState('disconnected');
      config.onError?.(errorMessage);
      completeQualificationRun(errorMessage);
    }
  }, [config]);

  // Start streaming (video/audio)
  const startStreaming = useCallback(
    async (videoElementId: string) => {
      if (!anamClientRef.current) {
        throw new Error('Anam client not initialized');
      }

      try {
        await anamClientRef.current.streamToVideoElement(videoElementId);
        setIsStreaming(true);
        recordQualificationStream(qualificationRunRef.current);
      } catch (err: unknown) {
        console.error('Anam stream error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error when starting Anam session';
        setError(errorMessage);
        config.onError?.(errorMessage);
        completeQualificationRun(errorMessage);
        throw err;
      }
    },
    [config]
  );

  // Interrupt speech synchronously before any recorder or report finalisation
  // awaits. This gives End Session an immediate, audible effect while allowing
  // the caller to keep the media streams alive long enough to flush final
  // recording chunks safely.
  const interruptPersona = useCallback(() => {
    const client = anamClientRef.current;
    if (!client) return;
    try {
      client.interruptPersona?.();
    } catch (err) {
      console.warn('Unable to interrupt avatar speech:', err);
    }
  }, []);

  // Stop streaming AND fully tear the session down. The Anam client exposes no
  // disconnect()/stop() — stopStreaming() is the teardown, and interruptPersona()
  // cuts off any in-progress utterance immediately. Without interrupting first,
  // the avatar can keep speaking a final response after the learner ends the
  // session; without nulling the ref, a lingering client keeps the session alive.
  const stopStreaming = useCallback(async () => {
    const client = anamClientRef.current;
    if (!client) return;
    try {
      try { client.interruptPersona?.(); } catch { /* ignore */ }
      await client.stopStreaming?.();
    } catch (err) {
      console.error('Error stopping stream:', err);
    } finally {
      anamClientRef.current = null;
      setIsStreaming(false);
      setIsConnected(false);
      setConnectionState('idle');
      config.onSessionEnd?.();
      completeQualificationRun();
    }
  }, [config]);

  // Send text message (if needed)
  const sendMessage = useCallback(
    async (message: string) => {
      if (anamClientRef.current && isStreaming) {
        await anamClientRef.current.sendTextMessage(message);
      }
    },
    [isStreaming]
  );

  // Get final transcript
  const getTranscript = useCallback(() => transcriptRef.current, []);

  // Clear the cached transcript for this session — call after a successful save
  // to the database so a future invocation with the same persistKey starts fresh.
  const clearCachedTranscript = useCallback(() => {
    if (!config.persistKey) return;
    try {
      localStorage.removeItem(TRANSCRIPT_STORAGE_PREFIX + config.persistKey);
    } catch {
      /* ignore */
    }
  }, [config.persistKey]);

  // Cleanup on unmount. The SDK has no disconnect() — the old `disconnect?.()`
  // was a silent no-op, so leaving a coaching/simulation page never tore the
  // session down and the avatar kept talking. Interrupt + stopStreaming is the
  // real teardown.
  useEffect(() => {
    const assistantUtterances = assistantUtterancesRef.current;
    const userUtterances = userUtterancesRef.current;
    return () => {
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      const client = anamClientRef.current;
      assistantUtterances.clear();
      userUtterances.clear();
      if (client) {
        try { client.interruptPersona?.(); } catch { /* ignore */ }
        try { void client.stopStreaming?.(); } catch { /* ignore */ }
        anamClientRef.current = null;
      }
    };
  }, []);

  return {
    initialize,
    startStreaming,
    interruptPersona,
    stopStreaming,
    sendMessage,
    getTranscript,
    clearCachedTranscript,
    isConnected,
    isStreaming,
    transcript,
    error,
    connectionState,
    lastDisconnectReason,
  };
}
